// shared-lib/cabinet-runs/segmentWall.js
//
// THE resolver for a stored cabinet-run segment (spec: claude-docs/
// 2026-09-05-cabinet-run-ownership-model.md §8). A run is placed in a ROOM,
// so a segment names a ROOM wall and resolves from the room alone:
//
//   real (no kind / kind:'real')  → the raw, deduped room wall by id; when the
//                                    id is gone, the room wall on the same line
//                                    as the segment's stored `wallFrame`
//   peninsula | island             → the layout's virtual wall (inline
//                                    alignment on `cabinetLayout.cabinetWalls`)
//   freestanding                   → null (the caller synthesizes its wall)
//
// Raw, never kitchen-clipped: params are measured from the room wall's own
// `bottomLeft`. The clip is a generator concern and stays in `toRuns.js`.
// Pure. No I/O.

const { dedupeRoomWalls } = require('../kitchen-assembly/wallDedupe');
const {
  buildCabinetWallResolver,
} = require('../kitchen-assembly/cabinetLayout/resolveCabinetWall');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { perpDistance, SAME_LINE_TOL } = require('./wallFrame');
const { pointToSegmentDistance } = require('./freestanding');

const VIRTUAL_KINDS = new Set(['peninsula', 'island']);

/** Param of `p` projected onto the wall's line (unclamped). */
function paramOf(wall, p) {
  const ax = getWallAxes(wall);
  return (p.x - ax.start.x) * ax.wallDir.x + (p.z - ax.start.z) * ax.wallDir.z;
}

/**
 * The room wall whose LINE carries both endpoints of `frame` (perpendicular
 * distance ≤ SAME_LINE_TOL), preferring the wall whose extent overlaps the
 * frame the most. Null when no wall lies on that line.
 */
function roomWallForFrame(frame, walls) {
  if (!frame?.a || !frame?.b) return null;
  let best = null;
  for (const w of walls || []) {
    const ax = getWallAxes(w);
    if (!ax) continue;
    if (perpDistance(w, frame.a) > SAME_LINE_TOL) continue;
    if (perpDistance(w, frame.b) > SAME_LINE_TOL) continue;
    const pa = paramOf(w, frame.a);
    const pb = paramOf(w, frame.b);
    const lo = Math.max(0, Math.min(pa, pb));
    const hi = Math.min(ax.wallLen, Math.max(pa, pb));
    const overlap = hi - lo; // may be ≤ 0 for a wall beside the frame on the same line
    if (!best || overlap > best.overlap) best = { wall: w, overlap };
  }
  return best ? best.wall : null;
}

/**
 * Build a resolver bound to one room. Dedupes the room walls ONCE; builds the
 * layout resolver lazily, since only peninsula / island segments need it.
 *
 * @param {Object} room room doc (`walls`, optional `cabinetLayout`)
 * @returns {{ resolve: (seg) => Object|null, walls: Object[], wallById: Map }}
 */
function buildSegmentWallResolver(room) {
  const walls = dedupeRoomWalls(room?.walls || []);
  const wallById = new Map(walls.map((w) => [w.id, w]));
  let layoutResolver = null;
  const layout = () =>
    layoutResolver ||
    (layoutResolver = buildCabinetWallResolver(room, room?.cabinetLayout));

  function resolve(seg) {
    if (!seg || seg.freestanding) return null;
    if (VIRTUAL_KINDS.has(seg.kind)) {
      return layout().resolve({ id: seg.wallId, kind: seg.kind });
    }
    const byId = seg.wallId ? wallById.get(seg.wallId) : null;
    if (byId) return byId;
    return seg.wallFrame ? roomWallForFrame(seg.wallFrame, walls) : null;
  }
  return { resolve, walls, wallById };
}

/** One-off resolve; prefer holding a resolver when resolving many segments. */
function resolveSegmentWall(segment, resolver) {
  return resolver ? resolver.resolve(segment) : null;
}

/**
 * Nearest room wall to a world point, with the projected on-wall point.
 * Same shape the web's deleted structure-wall `nearestWall` returned, so the
 * draw tool and the drop target swapped without changing their callers.
 *
 * `d` is the distance to the room wall's own `alignment` line, which IS the
 * room-interior face — a room wall is already one side of the structure wall.
 * The structure-wall snap this replaced measured to a CENTERLINE stepped
 * inward by half the wall's thickness, so a caller's snap band (e.g. the draw
 * tool's `SNAP_TOLERANCE`) is unchanged on a thin wall and shifts by ~half the
 * thickness on a thick one.
 */
function nearestRoomWall(point, room) {
  let best = null;
  for (const w of dedupeRoomWalls(room?.walls || [])) {
    const ax = getWallAxes(w);
    if (!ax) continue;
    const d = pointToSegmentDistance(point, ax.start, ax.end);
    if (best && d >= best.d) continue;
    const t = Math.max(0, Math.min(ax.wallLen, paramOf(w, point)));
    best = {
      d,
      wallId: w.id,
      position: {
        x: ax.start.x + t * ax.wallDir.x,
        z: ax.start.z + t * ax.wallDir.z,
      },
    };
  }
  return best;
}

module.exports = {
  buildSegmentWallResolver,
  resolveSegmentWall,
  roomWallForFrame,
  nearestRoomWall,
};
